import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import {
  COMPANY_LINKS,
  LOGO_FULL,
  NAV_LINKS,
  ROUTE_CONTACT,
  ROUTE_LOGIN,
} from "@/lib/brand";
import { StartBar } from "@/components/StartBar";

const slug = (label) =>
  label
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const TopNavbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Mobiles Menü schließt sich beim Seitenwechsel
  useEffect(() => setOpen(false), [pathname]);

  // ... und mit Escape. Die Seite dahinter wird bewusst nicht per
  // overflow: hidden gesperrt: Safari auf dem iPhone stellt danach teils eine
  // alte Scrollposition wieder her. Stattdessen hält overscroll-contain das
  // Scrollen im Menü.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      {/* Ein fixierter Block: oben die Startleiste über die volle Breite,
          darunter die Navigation, ab md rechts der Seitenleiste. Die Höhe
          des Ganzen steht als --header-h in src/index.css. */}
      <motion.header
        data-testid="top-navbar"
        initial={{ y: "-100%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        className="fixed top-0 right-0 left-0 z-50"
      >
        <StartBar />
        <div
          className={`h-20 md:ml-20 transition-colors duration-500 ${
            open
              ? "bg-navy-950 border-b border-white/10"
              : scrolled
                ? "bg-navy-950/70 backdrop-blur-xl border-b border-white/10"
                : "bg-transparent border-b border-transparent"
          }`}
        >
          {/* Ab lg ein Raster aus drei Spalten: die beiden 1fr-Spalten sind
              gleich breit, dadurch steht die Navigation mittig zur Kopfzeile.
              Wird der Platz knapp, weicht sie aus statt zu überlappen. */}
          <div className="h-full px-6 md:px-10 lg:px-16 flex items-center gap-6 lg:grid lg:grid-cols-[1fr_auto_1fr]">
            {/* Ab md übernimmt die Bildmarke der Seitenleiste - dort wäre die
                Wortmarke sonst das zweite Logo direkt daneben. */}
            <Link
              to="/"
              data-testid="navbar-logo-link"
              className="flex md:hidden items-center shrink-0"
            >
              <img
                src={LOGO_FULL}
                alt="MatchGate"
                className="h-8 md:h-9 w-auto object-contain"
              />
            </Link>

            <nav className="hidden lg:flex lg:col-start-2 items-center justify-center gap-7 xl:gap-9">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  data-testid={`navbar-link-${slug(link.label)}`}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                      isActive ? "text-white" : "text-white/60 hover:text-white"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            <div className="ml-auto lg:ml-0 lg:col-start-3 lg:justify-self-end flex items-center gap-3 shrink-0">
              <Link
                to={ROUTE_LOGIN}
                data-testid="navbar-login-link"
                className="hidden sm:inline text-sm font-medium text-white/70 hover:text-white transition-colors duration-300"
              >
                Login
              </Link>
              <Link
                to={ROUTE_CONTACT}
                data-testid="navbar-cta-button"
                className="group relative inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-brand-orange to-brand-blue shadow-[0_0_25px_-4px_rgba(255,107,0,0.6)] hover:shadow-[0_0_35px_-2px_rgba(0,122,255,0.7)] hover:scale-[1.04] transition-all duration-300"
              >
                Kontakt
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
              </Link>
              <button
                data-testid="navbar-mobile-toggle"
                onClick={() => setOpen((v) => !v)}
                className="lg:hidden text-white p-2 -mr-2"
                aria-label="Menü"
                aria-expanded={open}
              >
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            // Lenis soll Scrollen im Menü nicht abfangen
            data-lenis-prevent
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="fixed top-header -bottom-40 right-0 left-0 md:left-20 z-40 overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden bg-navy-950/95 backdrop-blur-xl lg:hidden"
          >
            {/* Das Menü reicht 10rem über den unteren Rand hinaus, damit es auf
                dem iPhone auch hinter der Safari-Leiste deckt; das Padding unten
                gleicht das aus. Oben die Hauptseiten, darunter die Seiten, die
                am Desktop nur im Footer stehen, dann der Login. Ein Pixel mehr
                als die Höhe, damit overscroll-contain immer greift. Die
                Scrollleiste ist ausgeblendet (am iPhone ohnehin unsichtbar). */}
            <nav className="min-h-[calc(100%+1px)] flex flex-col px-6 md:px-10 pt-6 pb-52">
              <div className="flex flex-col gap-4">
                {NAV_LINKS.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    end={link.to === "/"}
                    // Auch beim Tippen auf die aktuelle Seite schließen
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-link-${slug(link.label)}`}
                    className={({ isActive }) =>
                      `text-lg font-semibold transition-colors ${
                        isActive ? "text-white" : "text-white/80 hover:text-white"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-white/10 flex flex-col gap-4">
                {COMPANY_LINKS.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-link-${slug(link.label)}`}
                    className={({ isActive }) =>
                      `text-base font-medium transition-colors ${
                        isActive ? "text-white" : "text-white/60 hover:text-white"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>

              {/* In der Kopfzeile erst ab sm sichtbar, deshalb hier für Handys */}
              <Link
                to={ROUTE_LOGIN}
                onClick={() => setOpen(false)}
                data-testid="mobile-login-link"
                className="sm:hidden mt-6 pt-6 border-t border-white/10 text-base font-medium text-white/55 hover:text-white transition-colors"
              >
                Login
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
